import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, Phone, Mail, ExternalLink, CheckCircle2, Clock, Copy, Image as ImageIcon, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_COLOR = {
  'Lower Concern': 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
  'Moderate Concern': 'text-amber-400 border-amber-500/40 bg-amber-500/10',
  'Higher Concern': 'text-red-400 border-red-500/40 bg-red-500/10',
};

const FirstLookLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fulfilling, setFulfilling] = useState('');
  const token = localStorage.getItem('carlens_token');

  useEffect(() => {
    axios.get(`${API}/admin/firstlook`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLeads(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const [notes, setNotes] = useState({});

  const markDelivered = async (id) => {
    setFulfilling(id);
    try {
      await axios.post(`${API}/admin/firstlook/${id}/fulfill`, { specialistNotes: notes[id] || '' }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((prev) => prev.map((l) => l.id === id
        ? { ...l, purchase: { ...l.purchase, fulfillmentStatus: 'delivered', reviewedAt: new Date().toISOString(), specialistNotes: notes[id] || '' } } : l));
      toast.success('Marked as emailed');
    } catch {
      toast.error('Could not update. Please try again.');
    } finally {
      setFulfilling('');
    }
  };

  const copyDetails = (l) => {
    const lines = [
      `Vehicle: ${l.year || ''} ${l.make || ''} ${l.model || ''}`.trim(),
      `VIN: ${l.vin || 'not provided'}`,
      `Asking price: ${l.askingPrice || 'n/a'}`,
      `Mileage: ${l.mileage || 'n/a'}`,
      `Seller type: ${l.sellerType || 'n/a'}`,
      `Listing URL: ${l.listingUrl || 'not provided'}`,
      '',
      `Customer: ${l.firstName || ''} ${l.lastName || ''}`.trim(),
      `Email: ${l.email || ''}`,
      `Phone: ${l.phone || ''}`,
      `Preferred contact: ${l.preferredContact || ''}`,
      '',
      `Buyer notes: ${l.description || 'none'}`,
      `Photos submitted: ${(l.photos || []).length}`,
    ];
    navigator.clipboard.writeText(lines.join('\n'))
      .then(() => toast.success('Details copied — paste into Base44'))
      .catch(() => toast.error('Could not copy'));
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <div className="max-w-6xl mx-auto px-5 py-10">
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 text-sm" data-testid="fl-admin-back">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>
        <h1 className="text-3xl font-extrabold text-white flex items-center gap-3" style={{ fontFamily: 'Space Grotesk' }}>
          <Search className="w-7 h-7 text-orange-500" /> FirstLook™ Leads
        </h1>
        <p className="text-zinc-400 mt-2">{leads.length} total requests</p>

        {loading ? (
          <p className="text-zinc-500 py-16 text-center">Loading…</p>
        ) : leads.length === 0 ? (
          <p className="text-zinc-500 py-16 text-center">No FirstLook requests yet.</p>
        ) : (
          <div className="mt-8 space-y-5">
            {leads.map((l) => {
              const a = l.analysis || {};
              const paid = l.purchase?.paymentStatus === 'paid';
              const delivered = l.purchase?.fulfillmentStatus === 'delivered';
              return (
                <div key={l.id} className="rounded-2xl border border-zinc-800 bg-[#161618] p-6" data-testid={`fl-lead-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{l.year} {l.make} {l.model}</h3>
                      <p className="text-sm text-zinc-400 mt-0.5">{l.firstName} {l.lastName} · {l.sellerType} seller · asking {l.askingPrice || 'n/a'} · {l.mileage || 'n/a'} mi</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm">
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-zinc-300 hover:text-orange-400"><Phone className="w-3.5 h-3.5" /> {l.phone}</a>
                        <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-zinc-300 hover:text-orange-400"><Mail className="w-3.5 h-3.5" /> {l.email}</a>
                        {l.listingUrl && <a href={l.listingUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-zinc-300 hover:text-orange-400"><ExternalLink className="w-3.5 h-3.5" /> Listing</a>}
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">Prefers: {l.preferredContact} · {new Date(l.createdAt).toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {a.riskLevel && <span className={`text-xs font-bold px-3 py-1 rounded-full border ${RISK_COLOR[a.riskLevel] || ''}`}>{a.riskLevel}</span>}
                      {paid ? (
                        <>
                          <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/40">PAID · {l.purchase.product === 'remote' ? '$49 Report' : '$199 Inspection'}</span>
                          {delivered ? (
                            <span className="flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/40" data-testid={`fl-delivered-${l.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Finalized</span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid={`fl-pending-${l.id}`}><Clock className="w-3.5 h-3.5" /> Needs Review</span>
                          )}
                        </>
                      ) : (
                        <span className="text-xs px-3 py-1 rounded-full bg-zinc-700/40 text-zinc-400">{l.status}</span>
                      )}
                    </div>
                  </div>

                  {l.photos?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {l.photos.map((p, i) => (
                        <a key={i} href={`${API}/firstlook/files/${p.path}?auth=${token}`} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-zinc-700 block">
                          <img src={`${API}/firstlook/files/${p.path}?auth=${token}`} alt="lead" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}

                  {a.vehicleSnapshot && (
                    <details className="mt-4 group">
                      <summary className="cursor-pointer text-sm font-semibold text-orange-400">View AI analysis</summary>
                      <div className="mt-3 text-sm text-zinc-300 space-y-3">
                        <p>{a.vehicleSnapshot}</p>
                        {[['What Looks Positive', a.whatLooksPositive], ['Potential Warning Signs', a.potentialWarningSigns], ['Questions to Ask', a.questionsToAsk], ['Requires Physical Verification', a.requiresPhysicalVerification]].map(([t, items]) => (
                          <div key={t}><p className="font-semibold text-white">{t}</p><ul className="list-disc pl-5 text-zinc-400">{(items || []).map((it, i) => <li key={i}>{it}</li>)}</ul></div>
                        ))}
                        <p className="text-zinc-400"><span className="font-semibold text-white">Next step:</span> {a.recommendedNextStep}</p>
                      </div>
                    </details>
                  )}

                  {paid && (
                    <div className="mt-5 rounded-xl border border-blue-500/30 bg-blue-500/5 p-4" data-testid={`fl-submission-${l.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-semibold text-white flex items-center gap-2"><ImageIcon className="w-4 h-4 text-blue-300" /> Customer submission — build report in Base44</p>
                        <button onClick={() => copyDetails(l)} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/40 hover:bg-blue-500/30" data-testid={`fl-copy-${l.id}`}>
                          <Copy className="w-3.5 h-3.5" /> Copy all details
                        </button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-zinc-300 mb-3">
                        <p><span className="text-zinc-500">VIN:</span> {l.vin || <span className="text-zinc-600">not provided</span>}</p>
                        <p><span className="text-zinc-500">Asking:</span> {l.askingPrice || '—'}</p>
                        <p><span className="text-zinc-500">Mileage:</span> {l.mileage || '—'}</p>
                        <p><span className="text-zinc-500">Seller:</span> {l.sellerType || '—'}</p>
                        {l.listingUrl && <p className="sm:col-span-2 truncate"><span className="text-zinc-500">Listing:</span> <a href={l.listingUrl} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">{l.listingUrl}</a></p>}
                        {l.description && <p className="sm:col-span-2"><span className="text-zinc-500">Buyer notes:</span> {l.description}</p>}
                      </div>
                      {(l.photos || []).length > 0 ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-zinc-400">{l.photos.length} photo{l.photos.length === 1 ? '' : 's'} submitted:</p>
                            {l.photos.length > 1 && (
                              <a href={`${API}/admin/firstlook/${l.id}/photos.zip?token=${token}`} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/40 hover:bg-blue-500/30" data-testid={`fl-download-all-${l.id}`}>
                                <Download className="w-3.5 h-3.5" /> Download all (zip)
                              </a>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {l.photos.map((_, i) => {
                              const base = `${API}/admin/firstlook/${l.id}/photo/${i}?token=${token}`;
                              return (
                                <div key={i} className="relative group">
                                  <a href={base} target="_blank" rel="noreferrer">
                                    <img src={base} alt={`submission ${i + 1}`} className="w-24 h-24 object-cover rounded-lg border border-zinc-700 hover:border-blue-400 transition-colors" data-testid={`fl-photo-${l.id}-${i}`} />
                                  </a>
                                  <a href={`${base}&download=1`} className="absolute bottom-1 right-1 bg-black/70 rounded-md p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Download">
                                    <Download className="w-3.5 h-3.5 text-white" />
                                  </a>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-zinc-500">No photos submitted.</p>
                      )}
                    </div>
                  )}

                  {paid && l.purchase.product === 'remote' && !delivered && (
                    <div className="mt-5 rounded-xl border border-orange-500/30 bg-orange-500/5 p-4" data-testid={`fl-finalize-panel-${l.id}`}>
                      <p className="text-sm font-semibold text-white mb-1">Email this customer their Vehicle Decision Report</p>
                      <p className="text-xs text-zinc-400 mb-2">Build the report in Base44 using the submission above, email it to <span className="text-zinc-200">{l.email}</span>, then mark it emailed to update their status.</p>
                      <textarea
                        value={notes[l.id] || ''}
                        onChange={(e) => setNotes((prev) => ({ ...prev, [l.id]: e.target.value }))}
                        placeholder="Optional internal note (for your records)…"
                        rows={2}
                        className="w-full rounded-lg bg-[#111113] border border-zinc-700 text-sm text-zinc-200 p-3 focus:outline-none focus:border-orange-500"
                        data-testid={`fl-notes-${l.id}`}
                      />
                      <button onClick={() => markDelivered(l.id)} disabled={fulfilling === l.id} className="mt-3 text-sm font-semibold px-5 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50" data-testid={`fl-mark-delivered-${l.id}`}>
                        {fulfilling === l.id ? 'Saving…' : 'Mark Report as Emailed'}
                      </button>
                    </div>
                  )}
                  {delivered && l.purchase?.specialistNotes && (
                    <div className="mt-4 rounded-xl border border-zinc-700 bg-[#111113] p-3">
                      <p className="text-xs font-semibold text-zinc-400 mb-1">Internal note:</p>
                      <p className="text-sm text-zinc-300 whitespace-pre-line">{l.purchase.specialistNotes}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstLookLeads;
